import { useState } from 'react';
import { useNotification, useTranslation } from '@topcon/topcon-utils';
import { z } from 'zod';

import {
  useCreateRequestBusinessPartner,
  useCreateCompany,
  useCreateRequestBusinessPartnerProxy,
} from '../../pages/RequestBusinessPartner/businessPartnerRequest.mutations';
import {
  AddressInfo,
  RequestBusinessPartnerDivisionPostBody,
  RequestBusinessPartnerEndCustomerPostBody,
  RequestBusinessPartnerEndCustomerTypeEnum,
  ServiceAgreementTermsEnum,
  RequestBusinessPartnerProxyPostBody,
  RequestBusinessPartnerBody,
} from '../../api/generated/pm';
import {
  zAddressInfo,
  zRequestBusinessPartnerEndCustomerPostBody,
  zRequestBusinessPartnerProxyPostBody,
} from '../../api/generated/pm/zod.gen';
import { useErrorDrawer } from '../../providers/ErrorDrawerContext/error-drawer.context';
import { getInitialFormState } from '../../utils/form';

const FORM_NAME = 'CompanyDivisionForm';

const baseRequestBusinessPartnerFormSchema = z.object({
  name: zRequestBusinessPartnerEndCustomerPostBody.shape.name,
  type: z.nativeEnum(RequestBusinessPartnerEndCustomerTypeEnum),
  ...zAddressInfo.shape,
});

const requestBusinessPartnerFormSchema = baseRequestBusinessPartnerFormSchema.extend({
  serviceAgreementTerms: z.nativeEnum(ServiceAgreementTermsEnum),
  providerBusinessPartnerId: z.string(),
});

const proxyBusinessPartnerFormSchema = requestBusinessPartnerFormSchema.extend({
  email: zRequestBusinessPartnerProxyPostBody.shape.ownerEmail,
  /** The dealer's own reference for this customer. Optional by design. */
  providerCustomerNumber: zRequestBusinessPartnerProxyPostBody.shape.providerCustomerNumber,
});

/** API length limit for the Provider Customer Number, for the input's `maxLength`. */
const providerCustomerNumberMaxLength =
  proxyBusinessPartnerFormSchema.shape.providerCustomerNumber.unwrap().maxLength ?? undefined;

type RequestBusinessPartnerFormData = z.infer<typeof requestBusinessPartnerFormSchema>;
type ProxyBusinessPartnerFormData = z.infer<typeof proxyBusinessPartnerFormSchema>;

type RequestBusinessPartnerFormType = 'company' | 'division' | 'proxy';

interface UseBusinessPartnerFormProps {
  businessPartnerType: RequestBusinessPartnerFormType;
  defaultValues?: Partial<RequestBusinessPartnerFormData>;
  /**
   * The id of the Migration Candidate this request resolves (Add as New
   * Organization / Add as New Customer). Ties the new business partner
   * request back to the candidate so the backend can transition its status —
   * omitted entirely for ordinary create flows, which have no candidate.
   */
  migrationCandidateBusinessPartnerId?: string;
}

const DEFAULT_VALUES: Partial<RequestBusinessPartnerFormData> = {
  serviceAgreementTerms: ServiceAgreementTermsEnum.FULL_SERVICE,
  type: RequestBusinessPartnerEndCustomerTypeEnum.END_CUSTOMER_INDIVIDUAL,
};

function useBusinessPartnerForm({
  businessPartnerType,
  defaultValues = DEFAULT_VALUES,
  migrationCandidateBusinessPartnerId,
}: UseBusinessPartnerFormProps) {
  const { mutateAsync: createDivision, isPending: isPendingCreateDivision } =
    useCreateRequestBusinessPartner();
  const { mutateAsync: createCompany, isPending: isPendingCreateCompany } = useCreateCompany();
  const { mutateAsync: createProxy, isPending: isPendingCreateProxy } =
    useCreateRequestBusinessPartnerProxy();
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const { t } = useTranslation(['common']);
  const [formFields, setFormFields] = useState<
    RequestBusinessPartnerFormData | ProxyBusinessPartnerFormData
  >(
    getInitialFormState(
      businessPartnerType === 'proxy'
        ? proxyBusinessPartnerFormSchema
        : requestBusinessPartnerFormSchema,
      defaultValues
    )
  );

  const handleFormSubmit: () => Promise<
    RequestBusinessPartnerBody | undefined | void
  > = async () => {
    if (!validateForm(formFields)) {
      notificationService.addNotification(
        t('error', { ns: 'common' }),
        t('formValidationError', { ns: 'common' }),
        'error'
      );
      return;
    }
    const { name, type, serviceAgreementTerms, providerBusinessPartnerId, ...address } = formFields;

    try {
      if (businessPartnerType === 'company') {
        const companyParams: RequestBusinessPartnerEndCustomerPostBody = {
          name,
          companyAddress: address,
          currency: 'USD',
          providerBusinessPartnerId,
          serviceAgreementTerms,
          type,
          ...(migrationCandidateBusinessPartnerId && { migrationCandidateBusinessPartnerId }),
        };
        await createCompany(companyParams);
      } else if (businessPartnerType === 'division') {
        const divisionParams: RequestBusinessPartnerDivisionPostBody = {
          name,
          companyAddress: address,
          currency: 'USD',
          providerBusinessPartnerId,
          serviceAgreementTerms,
          type: RequestBusinessPartnerEndCustomerTypeEnum.END_CUSTOMER_ENTERPRISE,
        };
        await createDivision(divisionParams);
      } else if (businessPartnerType === 'proxy') {
        // `address` is whatever is left of the form, so the proxy-only fields
        // have to be lifted out of it or they travel as bogus address parts.
        const { email, providerCustomerNumber, ...companyAddress } = address as AddressInfo &
          Pick<ProxyBusinessPartnerFormData, 'email' | 'providerCustomerNumber'>;
        const proxyParams: RequestBusinessPartnerProxyPostBody = {
          name,
          companyAddress,
          currency: 'USD',
          ownerEmail: email,
          providerCustomerNumber: providerCustomerNumber?.trim() || undefined,
          serviceAgreementTerms,
          type,
          ...(migrationCandidateBusinessPartnerId && { migrationCandidateBusinessPartnerId }),
        };

        // 201 carries the created request; 202 (accepted for asynchronous
        // processing) carries no body and resolves `undefined`.
        return await createProxy(proxyParams);
      }
    } catch (err) {
      if (!showError(err)) {
        const errorMessage = err instanceof Error ? err.message : 'errorDescription';
        notificationService.addNotification(
          t('error', { ns: 'common' }),
          `${t('createFailed', { ns: 'common' })}: ${t(errorMessage, { ns: 'common' })}`,
          'error'
        );
      }
      return Promise.reject(err);
    }
  };

  const validateForm = (fields: RequestBusinessPartnerFormData | ProxyBusinessPartnerFormData) => {
    let formSchema: z.ZodSchema<RequestBusinessPartnerFormData | ProxyBusinessPartnerFormData> =
      requestBusinessPartnerFormSchema;
    const isProxy = isProxyFormData(fields);
    if (isProxy) {
      formSchema = proxyBusinessPartnerFormSchema;
    }
    const result = formSchema.safeParse(fields);
    return result.success;
  };

  const clearFields = () => {
    setFormFields(getInitialFormState(requestBusinessPartnerFormSchema, defaultValues));
  };

  const isPending = isPendingCreateDivision || isPendingCreateCompany || isPendingCreateProxy;

  return {
    formFields,
    setFormFields,
    validateForm,
    isPending,
    handleFormSubmit,
    clearFields,
    formName: FORM_NAME,
  };
}

function isProxyFormData(
  data: RequestBusinessPartnerFormData | ProxyBusinessPartnerFormData
): data is ProxyBusinessPartnerFormData {
  return 'email' in data && typeof data.email === 'string';
}

export {
  useBusinessPartnerForm,
  baseRequestBusinessPartnerFormSchema,
  requestBusinessPartnerFormSchema,
  providerCustomerNumberMaxLength,
  isProxyFormData,
  type RequestBusinessPartnerFormData,
  type ProxyBusinessPartnerFormData,
  type RequestBusinessPartnerFormType,
};
